import React, { useState } from "react";
import { Ban, KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SuspendPinCard({ onSuspended }) {
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("suspend"); // "suspend" | "unsuspend"
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    const clean = pin.replace(/\D/g, "");
    if (clean.length < 4) {
      toast.error("Ingresa un PIN válido (mínimo 4 dígitos)");
      return;
    }
    setSubmitting(true);
    try {
      const url = mode === "suspend"
        ? "/admin/users/suspend-by-pin"
        : "/admin/users/unsuspend-by-pin";
      await api.put(url, { pin: clean, reason: reason.trim() });
      toast.success(
        mode === "suspend"
          ? `PIN ${clean} suspendido correctamente`
          : `PIN ${clean} reactivado correctamente`
      );
      setPin("");
      setReason("");
      onSuspended?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo procesar la acción");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-suspend-pin-card"
      className="bg-white border-2 border-zinc-200 rounded-2xl p-5"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <h3 className="text-base font-black text-zinc-900">Suspender / reactivar PIN manualmente</h3>
          <p className="text-xs text-zinc-600">Bloquea o desbloquea el acceso de cualquier usuario por su PIN.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          data-testid="admin-suspend-pin-mode-suspend"
          onClick={() => setMode("suspend")}
          className={`flex-1 h-10 rounded-lg text-sm font-bold transition-all ${
            mode === "suspend" ? "bg-red-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          }`}
        >
          Suspender
        </button>
        <button
          data-testid="admin-suspend-pin-mode-unsuspend"
          onClick={() => setMode("unsuspend")}
          className={`flex-1 h-10 rounded-lg text-sm font-bold transition-all ${
            mode === "unsuspend" ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          }`}
        >
          Reactivar
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold text-zinc-700 mb-1 block">PIN del usuario</Label>
          <Input
            data-testid="admin-suspend-pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="Ej: 837984"
            inputMode="numeric"
            className="h-11 font-mono text-base tracking-widest rounded-lg border-2 border-zinc-300"
          />
        </div>
        {mode === "suspend" && (
          <div>
            <Label className="text-xs font-bold text-zinc-700 mb-1 block">Motivo (opcional)</Label>
            <Input
              data-testid="admin-suspend-pin-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Pago fraudulento confirmado"
              className="h-11 rounded-lg border-2 border-zinc-300"
            />
          </div>
        )}
      </div>

      <Button
        onClick={handleAction}
        disabled={submitting}
        data-testid="admin-suspend-pin-submit"
        className={`w-full h-12 mt-4 font-black rounded-xl text-white ${
          mode === "suspend" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
        }`}
      >
        {submitting ? (
          "Procesando…"
        ) : mode === "suspend" ? (
          <><Ban className="w-4 h-4 mr-2" /> Suspender este PIN</>
        ) : (
          <><ShieldCheck className="w-4 h-4 mr-2" /> Reactivar este PIN</>
        )}
      </Button>
    </div>
  );
}
